import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import { listFasonOrders, cancelFasonOrder } from "../../api/fasonOrders";
import FasonOrderModal from "../../components/FasonOrderModal";
import FasonStatusModal from "../../components/FasonStatusModal";

const STATUS_LABELS = {
  ordered: "Sipariş Verildi",
  at_printing: "Baskıda",
  received: "Teslim Alındı",
  cancelled: "İptal",
};

const STATUS_COLORS = {
  ordered: "warning",
  at_printing: "info",
  received: "success",
  cancelled: "default",
};

function daysBetween(dateStr) {
  if (!dateStr) return null;
  const then = new Date(dateStr);
  const now = new Date();
  const diffMs = now - then;
  if (diffMs < 0) return 0;
  return Math.floor(diffMs / (1000 * 60 * 60 * 24));
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleDateString("tr-TR");
}

function FasonTakipPage() {
  const [tab, setTab] = useState("active");
  const [activeList, setActiveList] = useState([]);
  const [historyList, setHistoryList] = useState([]);
  const [loadingActive, setLoadingActive] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [fasonOrderModalOpen, setFasonOrderModalOpen] = useState(false);
  const [statusModal, setStatusModal] = useState({ open: false, fasonOrder: null, mode: null });

  const loadActive = useCallback(async () => {
    setLoadingActive(true);
    setLoadError("");
    try {
      const data = await listFasonOrders({ statusGroup: "active", limit: 200 });
      setActiveList(Array.isArray(data?.items) ? data.items : []);
    } catch (err) {
      console.error("FasonTakipPage active load error:", err);
      setLoadError("Aktif fasonlar yüklenirken hata oluştu.");
    } finally {
      setLoadingActive(false);
    }
  }, []);

  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const data = await listFasonOrders({ statusGroup: "history", limit: 200 });
      setHistoryList(Array.isArray(data?.items) ? data.items : []);
    } catch (err) {
      console.error("FasonTakipPage history load error:", err);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    loadActive();
    loadHistory();
  }, [loadActive, loadHistory]);

  async function handleCancel(fo) {
    if (!window.confirm(`"${fo.fason_no}" fason siparişi iptal edilsin mi?`)) return;
    try {
      await cancelFasonOrder(fo.id);
      await loadActive();
      await loadHistory();
    } catch (err) {
      const msg =
        err?.response?.data?.error?.message ||
        err?.response?.data?.message ||
        err?.message ||
        "İptal edilemedi";
      alert(`Hata: ${msg}`);
    }
  }

  function openStatusModal(fo, mode) {
    setStatusModal({ open: true, fasonOrder: fo, mode });
  }

  async function handleStatusSaved() {
    await loadActive();
    await loadHistory();
  }

  async function handleOrderSaved() {
    await loadActive();
  }

  const rows = tab === "active" ? activeList : historyList;
  const isLoading = tab === "active" ? loadingActive : loadingHistory;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 800 }}>
          Fason Takip
        </Typography>
        <Button
          variant="contained"
          onClick={() => setFasonOrderModalOpen(true)}
        >
          + Yeni Fason Siparişi
        </Button>
      </Box>

      {loadError ? <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert> : null}

      {/* Tabs */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Aktif Fasonlar" value="active" />
        <Tab label="Geçmiş" value="history" />
      </Tabs>

      {/* Table */}
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 6 }}>
          <Typography variant="body1" color="text.secondary">
            {tab === "active" ? "Aktif fason siparişi yok." : "Geçmişte teslim alınmış fason yok."}
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 700, bgcolor: "grey.50" } }}>
                <TableCell>Fason No</TableCell>
                <TableCell>İş Emri</TableCell>
                <TableCell>Tedarikçi</TableCell>
                <TableCell>Hammadde</TableCell>
                <TableCell>Çıktı Malzeme / Varyant</TableCell>
                <TableCell>Durum</TableCell>
                <TableCell align="right">Sip. Tabaka</TableCell>
                <TableCell align="right">Bıçak</TableCell>
                <TableCell align="right">Sip. Tabak</TableCell>
                <TableCell align="right">Baskıda Mik.</TableCell>
                <TableCell align="right">Teslim Mik.</TableCell>
                <TableCell align="right">Kayıp (tabaka)</TableCell>
                <TableCell align="right">Baskıda Gün</TableCell>
                <TableCell>Sipariş Tarihi</TableCell>
                <TableCell>Baskıya Giriş</TableCell>
                <TableCell>Teslim Tarihi</TableCell>
                {tab === "active" ? <TableCell align="center">İşlem</TableCell> : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((fo) => {
                const loss =
                  fo.status === "received" &&
                  fo.qty_at_printing != null &&
                  fo.qty_received != null
                    ? Number(fo.qty_at_printing) - Number(fo.qty_received)
                    : null;
                const daysAtPrinting =
                  fo.status === "at_printing" ? daysBetween(fo.arrived_at_printing_date) : null;

                return (
                  <TableRow key={fo.id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{fo.fason_no}</TableCell>
                    <TableCell>
                      <Tooltip title={fo.product_name || ""}>
                        <span>{fo.production_order_no || fo.production_order_id}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{fo.supplier_name || "—"}</TableCell>
                    <TableCell>
                      <Tooltip title={fo.input_material_name || ""}>
                        <span>
                          {fo.input_material_code || fo.input_material_id}
                          {fo.input_variant_display_name ? (
                            <Typography variant="caption" display="block" color="text.secondary">
                              {fo.input_variant_display_name}
                            </Typography>
                          ) : null}
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={fo.output_material_name || ""}>
                        <span>
                          {fo.output_material_code || fo.output_material_id}
                          {fo.output_variant_display_name ? (
                            <Typography variant="caption" display="block" color="text.secondary">
                              {fo.output_variant_display_name}
                            </Typography>
                          ) : null}
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={STATUS_LABELS[fo.status] || fo.status}
                        color={STATUS_COLORS[fo.status] || "default"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">{fo.qty_ordered ?? "—"}</TableCell>
                    <TableCell align="right">
                      {fo.plates_per_sheet != null ? fo.plates_per_sheet : "—"}
                    </TableCell>
                    <TableCell align="right">
                      {fo.qty_ordered_plates != null
                        ? fo.qty_ordered_plates.toLocaleString("tr-TR")
                        : fo.qty_ordered && fo.plates_per_sheet
                        ? (Number(fo.qty_ordered) * Number(fo.plates_per_sheet)).toLocaleString("tr-TR")
                        : "—"}
                    </TableCell>
                    <TableCell align="right">{fo.qty_at_printing ?? "—"}</TableCell>
                    <TableCell align="right">{fo.qty_received ?? "—"}</TableCell>
                    <TableCell align="right">
                      {loss != null ? (
                        <Typography
                          variant="body2"
                          color={loss > 0 ? "error" : "success.main"}
                          fontWeight={loss > 0 ? 700 : 400}
                        >
                          {loss}
                        </Typography>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {daysAtPrinting != null ? (
                        <Chip label={`${daysAtPrinting} gün`} size="small" color={daysAtPrinting > 7 ? "warning" : "default"} />
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>{formatDate(fo.order_date)}</TableCell>
                    <TableCell>{formatDate(fo.arrived_at_printing_date)}</TableCell>
                    <TableCell>{formatDate(fo.received_date)}</TableCell>
                    {tab === "active" ? (
                      <TableCell align="center">
                        <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center", flexWrap: "nowrap" }}>
                          {fo.status === "ordered" && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="primary"
                              onClick={() => openStatusModal(fo, "at_printing")}
                            >
                              Baskıya Ulaştı
                            </Button>
                          )}
                          {fo.status === "at_printing" && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="success"
                              onClick={() => openStatusModal(fo, "received")}
                            >
                              Teslim Alındı
                            </Button>
                          )}
                          <Button
                            size="small"
                            variant="text"
                            color="error"
                            onClick={() => handleCancel(fo)}
                          >
                            İptal
                          </Button>
                        </Box>
                      </TableCell>
                    ) : null}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Modals */}
      {fasonOrderModalOpen && (
        <FasonOrderModal
          onClose={() => setFasonOrderModalOpen(false)}
          onSaved={handleOrderSaved}
        />
      )}

      {statusModal.open && (
        <FasonStatusModal
          fasonOrder={statusModal.fasonOrder}
          mode={statusModal.mode}
          onClose={() => setStatusModal({ open: false, fasonOrder: null, mode: null })}
          onSaved={handleStatusSaved}
        />
      )}
    </Box>
  );
}

export default FasonTakipPage;
